import { useCallback, useId, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { WageComposition } from "./wageComposition";

/**
 * Whether the rule lines are unfolded, and the ids that tie the toggle to the regions it folds. A breakdown
 * opens as a summary of its segments with the rule lines folded, unless it is reopened by "Back to wage
 * breakdown" after being left unfolded (`opensWithDetails`); the viewer's choice then wins.
 */
export const useCompositionFold = (composition: WageComposition | undefined, opensWithDetails: boolean) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const idPrefix = useId();
	// DEFAULT: no composition yet (still loading) counts as zero segments — nothing to fold until they arrive.
	const segmentCount = composition?.segments.length ?? 0;
	const regionIdOf = useCallback((rowId: number) => `${idPrefix}composition-${rowId}`, [idPrefix]);
	// Only segments that actually render a composition region are named.
	const controls = (composition?.segments ?? [])
		.flatMap((segment) => {
			const folds = segment.type === "paid" ? segment.lines !== null : segment.type === "break" && segment.row.contributions !== null;
			return folds ? [regionIdOf(segment.row.id)] : [];
		})
		.join(" ");
	// Folds only when something can fold: an all-legacy breakdown (no rule lines until its next
	// recalculation) would otherwise show a toggle that changes nothing.
	const collapsible = controls !== "";
	// `null` = not chosen yet, so data arriving after mount still gets the right default. A breakdown the viewer
	// left unfolded starts as that choice.
	const [chosenOpen, setChosenOpen] = useState<boolean | null>(opensWithDetails ? true : null);
	// Only a breakdown with a toggle honours the choice: one folded earlier must not keep its lines hidden,
	// with no toggle left to open them, once a refetch leaves nothing to fold.
	const open = collapsible ? (chosenOpen ?? false) : true;

	const toggle = useCallback(() => {
		setChosenOpen(!open);
		track("wage_breakdown_composition_toggled", { department_id: chosenDepartment.department_id, expanded: !open, segment_count: segmentCount });
	}, [open, track, chosenDepartment.department_id, segmentCount]);

	return { collapsible, open, toggle, regionIdOf, controls };
};
